import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import type maplibregl from 'maplibre-gl'
import { Crosshair, MapPin, Search, X, AlertTriangle, Loader2 } from 'lucide-react'
import { MapContainer, DraggableMarker, SITE_ZOOM } from 'features/Map'
import { AddressAutocomplete } from 'components/AddressAutocomplete'
import { Button } from 'components/ui/button'
import { cn } from 'lib/utils'
import { reverseGeocodeCoords } from 'slices/evaluationSiteSlice'
import {
	fieldsFromAddressResult,
	fieldsFromGeocodeResult,
	suggestedSiteName,
	type CapturedSiteFields,
} from 'utils/siteCaptureFields'
import { createCapture } from '../capture/captureTypes'
import { submitCapture } from '../capture/submitCapture'
import { createFieldHandlers } from '../capture/handlers'
import useCurrentPosition from '../hooks/useCurrentPosition'
import AccuracyCircle from '../components/AccuracyCircle'
import { formatDistance } from '../utils/distance'
import { fieldRoutes } from 'constants/fieldRoutes'

/**
 * Capture a site: by standing on it, or by typing its address.
 *
 * One screen rather than two, because those are two ways into the same
 * confirmation, not two flows. The map is the screen; the address search is a
 * pill over it.
 *
 * This is the capability with no workaround today. A pad, a parcel or a corner
 * with no street address cannot be recorded at all — `LocationAddNewCN` demands
 * five address fields plus a photo, and its lat/lng inputs are disabled.
 */
const NewSite = () => {
	const navigate = useNavigate()
	const dispatch = useDispatch()
	const token = useSelector((state: any) => state.userLogin?.userInfo?.token)

	const [map, setMap] = useState<maplibregl.Map | null>(null)
	const [pin, setPin] = useState<{ lng: number; lat: number } | null>(null)
	const [searchOpen, setSearchOpen] = useState(false)
	const [fields, setFields] = useState<CapturedSiteFields | null>(null)
	const [name, setName] = useState('')
	const [saving, setSaving] = useState(false)
	const [error, setError] = useState<string | null>(null)
	const [noAddress, setNoAddress] = useState(false)

	const lastResultRef = useRef<any>(null)
	const pinMovedRef = useRef(false)
	const didPinRef = useRef(false)
	const didCenterRef = useRef(false)
	// Which capture route produced this pin, so the record says how it was made.
	const viaRef = useRef<'gps' | 'map' | 'address'>('gps')

	const { position, status, error: positionError, request } = useCurrentPosition()

	// Ask on mount. The whole point of the screen is "record where I am", so
	// making the user tap first would be ceremony.
	useEffect(() => request(), [request])

	// Drop the pin on the first fix, once.
	useEffect(() => {
		if (!position || didPinRef.current) return
		didPinRef.current = true
		viaRef.current = 'gps'
		setPin({ lng: position.longitude, lat: position.latitude })
	}, [position])

	/*
	 * Centre once, as soon as BOTH the fix and the map exist.
	 *
	 * Kept apart from dropping the pin because the two become available in
	 * either order, and a warm phone usually returns a fix before MapLibre has
	 * finished loading its style. Marking the job done and then calling
	 * `map?.flyTo` — with the map still null — left the pin correct and the
	 * camera sitting at the default: the whole United States at zoom 4, with
	 * the user pinching their way down to their own car park.
	 *
	 * MapContainer's `center`/`zoom` props cannot cover for this: `useMapLibre`
	 * captures them with `useRef` on first render, when there is no pin yet.
	 *
	 * Guarded so it fires only the first time. Re-centring on every pin change
	 * would yank the camera away from a pin the user had just dragged.
	 */
	useEffect(() => {
		if (!map || !pin || didCenterRef.current) return
		didCenterRef.current = true
		map.flyTo({ center: [pin.lng, pin.lat], zoom: SITE_ZOOM, duration: 0 })
	}, [map, pin])

	// Tap to place, so a fix that is off by 40m is still workable.
	useEffect(() => {
		if (!map) return
		const handleClick = (e: maplibregl.MapMouseEvent) => {
			pinMovedRef.current = true
			viaRef.current = 'map'
			setPin({ lng: e.lngLat.lng, lat: e.lngLat.lat })
		}
		map.on('click', handleClick)
		return () => {
			try {
				map.off('click', handleClick)
			} catch {
				/* Map already destroyed. */
			}
		}
	}, [map])

	const recentre = useCallback(() => {
		if (position) {
			viaRef.current = 'gps'
			pinMovedRef.current = false
			setPin({ lng: position.longitude, lat: position.latitude })
			map?.flyTo({ center: [position.longitude, position.latitude], zoom: SITE_ZOOM })
		} else {
			request()
		}
	}, [position, map, request])

	const handleAddressSelect = useCallback(
		(result: any) => {
			lastResultRef.current = result
			pinMovedRef.current = false
			viaRef.current = 'address'
			setSearchOpen(false)
			setPin({ lng: result.longitude, lat: result.latitude })
			map?.flyTo({ center: [result.longitude, result.latitude], zoom: SITE_ZOOM, duration: 600 })
		},
		[map],
	)

	/** Resolve what is at the pin, then show the confirm sheet. */
	const describePin = useCallback(async () => {
		if (!pin) return
		setError(null)
		setNoAddress(false)

		// Pin untouched since an address pick — reuse what we already have
		// rather than spending a reverse geocode to learn the same thing.
		if (!pinMovedRef.current && lastResultRef.current) {
			const next = fieldsFromAddressResult(lastResultRef.current, pin)
			setFields(next)
			setName(suggestedSiteName(next))
			return
		}

		const action: any = await (dispatch as any)(
			reverseGeocodeCoords({ lat: pin.lat, lng: pin.lng }),
		)
		const result = action?.meta?.requestStatus === 'fulfilled' ? action.payload : null
		const next = fieldsFromGeocodeResult(result, pin)
		setFields(next)
		setName(suggestedSiteName(next))
		// Not an error: plenty of these sites have no address to find.
		if (!result) setNoAddress(true)
	}, [dispatch, pin])

	const save = useCallback(async () => {
		if (!fields || !name.trim()) return
		setSaving(true)
		setError(null)

		// county is a UI hint, not a site field — the model has no column for it
		// and it is not a city. Dropped here as well as by the server whitelist.
		const { county: _county, ...saveable } = fields

		const capture = createCapture('site', {
			...saveable,
			name: name.trim(),
			capturedVia: viaRef.current,
			// Only meaningful when the pin is still where the device put it.
			gpsAccuracy: viaRef.current === 'gps' ? position?.accuracy : undefined,
		})

		const result = await submitCapture(capture, createFieldHandlers(token))
		setSaving(false)

		if (result.status === 'sent') {
			// Near Me rather than the home: the site just captured is now the
			// nearest thing, at the top of that list, which is the confirmation.
			navigate(fieldRoutes.NEAR)
		} else {
			// The capture survives on screen so nothing typed is lost.
			setError(result.error ?? 'Could not save this site.')
		}
	}, [fields, name, position, token, navigate])

	const locating = status === 'locating' && !pin

	/*
	 * Past this, the circle is wider than the site.
	 *
	 * GPS outdoors is 5-20m, and 20-50m between buildings — all workable, since
	 * the pin can be dragged. 200m is where it stops describing a parcel at all:
	 * WiFi-only positioning, or iOS's deliberately coarse mode, which reports
	 * kilometres. Only meaningful for a fix the device produced; a pin the user
	 * placed carries no accuracy claim.
	 */
	const unusableFix =
		viaRef.current === 'gps' && position != null && position.accuracy > 200

	return (
		<div className="relative h-[100dvh] overflow-hidden bg-gray-100">
			{/* Full-bleed and outside any scroll container: a map inside a scroller
			    fights the page for touch. */}
			<MapContainer
				center={pin ? [pin.lng, pin.lat] : undefined}
				zoom={pin ? SITE_ZOOM : undefined}
				onMapLoad={setMap}
				hideBasemapSwitcher
				// Imagery, not street names. You are standing on the thing: what
				// matters is the parcel outline, the frontage, where the parking
				// wraps — none of which a street map draws. Only applies when the
				// user has never picked a basemap; their own choice still wins.
				defaultBasemap="satellite"
				className="absolute inset-0 rounded-none"
			>
				{pin && (
					<DraggableMarker
						map={map}
						longitude={pin.lng}
						latitude={pin.lat}
						onDragEnd={({ lng, lat }) => {
							pinMovedRef.current = true
							viaRef.current = 'map'
							setPin({ lng, lat })
						}}
					/>
				)}
				{/* Only while the pin is the device's own idea of where it is. */}
				{pin && viaRef.current === 'gps' && (
					<AccuracyCircle
						map={map}
						latitude={position?.latitude ?? null}
						longitude={position?.longitude ?? null}
						accuracy={position?.accuracy ?? null}
					/>
				)}
			</MapContainer>

			{/* Top bar: close, and the address search pill. */}
			<div
				className="absolute top-0 left-0 right-0 z-10 flex items-center gap-2 p-3"
				style={{ paddingTop: 'max(0.75rem, env(safe-area-inset-top))' }}
			>
				<button
					type="button"
					onClick={() => navigate(fieldRoutes.HOME)}
					aria-label="Cancel"
					className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-white shadow-md text-gray-600"
				>
					<X className="w-5 h-5" />
				</button>

				{searchOpen ? (
					<div className="flex-1 rounded-lg bg-white shadow-md p-2">
						<AddressAutocomplete onSelect={handleAddressSelect} placeholder="Search an address" />
					</div>
				) : (
					<button
						type="button"
						onClick={() => setSearchOpen(true)}
						className="flex-1 inline-flex items-center gap-2 h-11 rounded-full bg-white shadow-md px-4 text-sm text-gray-500"
					>
						<Search className="w-4 h-4" />
						Search an address
					</button>
				)}
			</div>

			{/* Recentre. Also the retry when a fix failed. */}
			<button
				type="button"
				onClick={recentre}
				aria-label="Use my location"
				className="absolute right-3 z-10 inline-flex items-center justify-center w-11 h-11 rounded-full bg-white shadow-md text-primary"
				// 72% clears the sheet, which is capped at 70dvh.
				style={{ bottom: fields ? '72%' : '13rem' }}
			>
				<Crosshair className="w-5 h-5" />
			</button>

			{/* Bottom sheet: position summary, then the confirm form. */}
			<div
				className="absolute bottom-0 left-0 right-0 z-10 bg-white rounded-t-2xl shadow-2xl max-h-[70dvh] overflow-y-auto"
				style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}
			>
				<div className="px-4 pt-4 space-y-3">
					{locating && (
						<p className="flex items-center gap-2 text-sm text-gray-500">
							<Loader2 className="w-4 h-4 animate-spin" />
							Finding your location…
						</p>
					)}

					{positionError && !pin && (
						<div className="rounded-lg bg-amber-50 border border-amber-200 p-3">
							<p className="flex items-start gap-2 text-sm text-amber-800">
								<AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
								{positionError}
							</p>
							<p className="mt-1 text-xs text-amber-700">
								You can still search an address above, or tap the map to place a pin.
							</p>
						</div>
					)}

					{pin && !fields && (
						<>
							<div className="flex items-baseline justify-between">
								<p className="text-sm font-medium text-gray-800">
									{viaRef.current === 'gps' ? 'Your location' : 'Pin placed'}
								</p>
								{viaRef.current === 'gps' && position && (
									// Stated plainly: a bare pin implies precision the device
									// does not have. In the units a person reads — "±4828 m" is
									// technically the answer and practically unreadable.
									<p
										className={cn(
											'text-xs',
											unusableFix ? 'font-medium text-amber-700' : 'text-gray-500',
										)}
									>
										±{formatDistance(position.accuracy)}
									</p>
								)}
							</div>

							{unusableFix ? (
								/*
								 * A fix this vague is not a location, and offering "Use this
								 * spot" as the blue button invites saving a site miles from
								 * where the user is standing. Reported here at ±3 mi on an
								 * iPhone with Precise Location switched off.
								 */
								<div className="rounded-lg bg-amber-50 border border-amber-200 p-3">
									<p className="flex items-start gap-2 text-sm text-amber-800">
										<AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
										This is too vague to record as a site.
									</p>
									<ul className="mt-1.5 ml-6 list-disc text-xs text-amber-700 space-y-0.5">
										<li>Step into the open, away from a roof, and tap Use my location.</li>
										<li>
											A radius this wide usually means <b>Precise Location</b> is off
											for this site in iOS Settings → Privacy &amp; Security → Location
											Services.
										</li>
										<li>Or tap the map to place the pin yourself.</li>
									</ul>
								</div>
							) : (
								<p className="text-xs text-gray-500">
									Drag the pin or tap the map to place it exactly.
								</p>
							)}

							<Button
								// Demoted, not removed. Someone who knows the pin is right —
								// they dragged it, or the parcel is the only thing for a mile
								// — should not be blocked by our opinion of their GPS.
								variant={unusableFix ? 'ghost' : 'primary'}
								isInput
								onClick={describePin}
								className="w-full"
							>
								<MapPin className="w-4 h-4 mr-2" />
								{unusableFix ? 'Use this spot anyway' : 'Use this spot'}
							</Button>
						</>
					)}

					{fields && (
						<>
							<label className="block">
								<span className="text-xs font-medium text-gray-500">Site name</span>
								<input
									value={name}
									onChange={e => setName(e.target.value)}
									className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
									placeholder="Name this site"
								/>
							</label>

							{/* Editable, not a summary. The geocoder is thin on exactly the
							    sites this screen exists for — rural parcels, new pads, roads
							    with no house numbers in OSM — and the user standing there is
							    the one person who knows the real address. Correcting it
							    later, from a desk, means remembering which pin was which. */}
							<label className="block">
								<span className="text-xs font-medium text-gray-500">Address</span>
								<input
									value={fields.address ?? ''}
									onChange={e => setFields({ ...fields, address: e.target.value })}
									className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
									placeholder="Street address"
									autoComplete="off"
								/>
							</label>

							<label className="block">
								<span className="text-xs font-medium text-gray-500">City</span>
								<input
									value={fields.city ?? ''}
									onChange={e => setFields({ ...fields, city: e.target.value })}
									className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
									// The county is the only locality a rural result has. Shown as
									// a hint so the user knows roughly where the pin landed, never
									// stored — a county is not a city.
									placeholder={fields.county ? `${fields.county} County` : 'City'}
									autoComplete="off"
								/>
							</label>

							{/* State on its own row rather than squeezed beside the city:
							    the geocoder returns "California", not "CA", and a field too
							    narrow to show its own value reads as broken. */}
							<div className="flex gap-2">
								<label className="block flex-1">
									<span className="text-xs font-medium text-gray-500">State</span>
									<input
										value={fields.state ?? ''}
										onChange={e => setFields({ ...fields, state: e.target.value })}
										className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
										autoComplete="off"
									/>
								</label>
								<label className="block w-28">
									<span className="text-xs font-medium text-gray-500">ZIP</span>
									<input
										value={fields.postalCode ?? ''}
										onChange={e => setFields({ ...fields, postalCode: e.target.value })}
										className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
										inputMode="numeric"
										autoComplete="off"
									/>
								</label>
							</div>

							<p className="text-xs text-gray-400">
								{fields.latitude.toFixed(5)}, {fields.longitude.toFixed(5)}
							</p>

							{noAddress && (
								<p className="text-xs text-amber-700">
									No address found here. The coordinates are saved — fill in whatever you
									know.
								</p>
							)}

							{error && <p className="text-xs text-destructive">{error}</p>}

							<div className="flex gap-2">
								<Button
									variant="ghost"
									isInput
									onClick={() => setFields(null)}
									className="flex-1"
								>
									Back
								</Button>
								<Button
									variant="primary"
									isInput
									onClick={save}
									className="flex-1"
									disabled={saving || !name.trim()}
								>
									{saving ? 'Saving…' : 'Save site'}
								</Button>
							</div>
						</>
					)}
				</div>
			</div>
		</div>
	)
}

export default NewSite
